/*
 * Shared WebGL toolkit for every Adam scene: one render loop for all
 * canvases, soft glowing points, additive lines and screen-space labels.
 * Scenes that scroll out of view stop rendering.
 */
import {
  AdditiveBlending, BufferAttribute, BufferGeometry, Color, Group, IcosahedronGeometry,
  LineBasicMaterial, LineLoop, LineSegments, MathUtils, Mesh, MeshBasicMaterial,
  OctahedronGeometry, PerspectiveCamera, Points, Scene, ShaderMaterial, Vector3, WebGLRenderer
} from 'three';

// Only what the scenes use, so the rest of three.js is tree-shaken
const THREE = {
  AdditiveBlending, BufferAttribute, BufferGeometry, Color, Group, IcosahedronGeometry,
  LineBasicMaterial, LineLoop, LineSegments, MathUtils, Mesh, MeshBasicMaterial,
  OctahedronGeometry, PerspectiveCamera, Points, Scene, ShaderMaterial, Vector3, WebGLRenderer
};

const reducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)"
).matches;

const palette = {
  cyan: new THREE.Color(0x62f4e5),
  blue: new THREE.Color(0x718cff),
  violet: new THREE.Color(0xb18cff),
  amber: new THREE.Color(0xffc56b),
  pink: new THREE.Color(0xff8fc7),
  white: new THREE.Color(0xeefcff),
  slate: new THREE.Color(0x56627b)
};

/*
 * Points sized in world units with a soft round glow.
 */
const pointShader = {
  vertexShader: `
    attribute vec3 aColor;
    attribute float aSize;
    attribute float aAlpha;
    uniform float uScale;
    varying vec3 vColor;
    varying float vAlpha;

    void main() {
      vec4 mv = modelViewMatrix * vec4(position, 1.0);
      gl_PointSize = max(aSize * uScale / -mv.z, 1.0);
      gl_Position = projectionMatrix * mv;
      vColor = aColor;
      vAlpha = aAlpha;
    }
  `,
  fragmentShader: `
    varying vec3 vColor;
    varying float vAlpha;

    void main() {
      float d = length(gl_PointCoord - 0.5);
      float glow = pow(smoothstep(0.5, 0.0, d), 1.7);
      float core = smoothstep(0.16, 0.0, d);
      float a = (glow + core * 0.5) * vAlpha;
      if (a < 0.004) discard;
      gl_FragColor = vec4(vColor + core * 0.35, a);
    }
  `
};

const stages = [];

function createStage(canvas, { fov = 40, z = 9, y = 0, fit = 1.2 } = {}) {
  let renderer;

  try {
    renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      powerPreference: "high-performance"
    });
  } catch (error) {
    console.warn("WebGL initialization failed.", error);
    return null;
  }

  const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.75);
  renderer.setPixelRatio(pixelRatio);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(fov, 1, 0.1, 100);
  const cameraHome = new THREE.Vector3(0, y, z);

  const stage = {
    canvas,
    renderer,
    scene,
    camera,
    scale: { value: 1 },
    width: 1,
    height: 1,
    time: 0,
    visible: false,
    pointer: { x: 0, y: 0, tx: 0, ty: 0 },
    update: null,

    start(update) {
      stage.update = update;
      if (reducedMotion) stage.refresh(480);
    },

    /*
     * With reduced motion there is no loop, so advance the
     * simulation off-screen and draw a single still frame.
     */
    refresh(steps = 120) {
      if (!reducedMotion || !stage.update) return;
      for (let i = 0; i < steps; i++) {
        stage.time += 1 / 60;
        stage.update(1 / 60, stage.time);
      }
      stage.update(0, stage.time);
      renderer.render(scene, camera);
    }
  };

  function resize() {
    const width = canvas.clientWidth || 600;
    const height = canvas.clientHeight || 400;

    stage.width = width;
    stage.height = height;

    renderer.setSize(width, height, false);
    camera.aspect = width / height;

    // Pull the camera back on narrow screens so nothing is cropped.
    const zoom = Math.max(1, fit / camera.aspect);
    camera.position.copy(cameraHome).multiplyScalar(zoom);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();

    stage.scale.value =
      height * pixelRatio / (2 * Math.tan(THREE.MathUtils.degToRad(fov) / 2));

    stage.refresh(0);
  }

  if ("ResizeObserver" in window) {
    new ResizeObserver(resize).observe(canvas);
  } else {
    window.addEventListener("resize", resize);
  }

  resize();

  if ("IntersectionObserver" in window) {
    new IntersectionObserver(entries => {
      stage.visible = entries[0].isIntersecting;
    }, { rootMargin: "120px 0px" }).observe(canvas);
  } else {
    stage.visible = true;
  }

  stages.push(stage);
  return stage;
}

window.addEventListener("pointermove", event => {
  for (const stage of stages) {
    if (!stage.visible) continue;
    const rect = stage.canvas.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    stage.pointer.tx = Math.max(-1.5, Math.min(1.5, x));
    stage.pointer.ty = Math.max(-1.5, Math.min(1.5, y));
  }
}, { passive: true });

let last = 0;

function frame(now) {
  const dt = last ? Math.min((now - last) / 1000, 1 / 20) : 1 / 60;
  last = now;

  for (const stage of stages) {
    if (!stage.visible || !stage.update) continue;

    const pointer = stage.pointer;
    pointer.x += (pointer.tx - pointer.x) * 0.04;
    pointer.y += (pointer.ty - pointer.y) * 0.04;

    stage.time += dt;
    stage.update(dt, stage.time);
    stage.renderer.render(stage.scene, stage.camera);
  }

  requestAnimationFrame(frame);
}

if (!reducedMotion) requestAnimationFrame(frame);

function glowPoints(stage, count) {
  const geometry = new THREE.BufferGeometry();

  const attribute = (name, itemSize, fill) => {
    const array = new Float32Array(count * itemSize).fill(fill);
    geometry.setAttribute(name, new THREE.BufferAttribute(array, itemSize));
    return array;
  };

  const position = attribute("position", 3, 0);
  const color = attribute("aColor", 3, 1);
  const size = attribute("aSize", 1, 0.05);
  const alpha = attribute("aAlpha", 1, 1);

  const material = new THREE.ShaderMaterial({
    uniforms: { uScale: stage.scale },
    vertexShader: pointShader.vertexShader,
    fragmentShader: pointShader.fragmentShader,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending
  });

  const points = new THREE.Points(geometry, material);
  points.frustumCulled = false;

  return {
    points,
    position,
    color,
    size,
    alpha,
    count,

    setPosition(i, v) {
      position[i * 3] = v.x;
      position[i * 3 + 1] = v.y;
      position[i * 3 + 2] = v.z;
    },

    setColor(i, c, k = 1) {
      color[i * 3] = c.r * k;
      color[i * 3 + 1] = c.g * k;
      color[i * 3 + 2] = c.b * k;
    },

    flush() {
      for (const key in geometry.attributes) {
        geometry.attributes[key].needsUpdate = true;
      }
    }
  };
}

/*
 * Additive line segments. Fade a segment by darkening its colour.
 */
function glowLines(maxSegments) {
  const geometry = new THREE.BufferGeometry();
  const position = new Float32Array(maxSegments * 6);
  const color = new Float32Array(maxSegments * 6);

  geometry.setAttribute("position", new THREE.BufferAttribute(position, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(color, 3));

  const lines = new THREE.LineSegments(
    geometry,
    new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    })
  );

  lines.frustumCulled = false;

  return {
    lines,
    position,
    color,
    max: maxSegments,

    setSegment(i, a, b, ca, ka = 1, cb = ca, kb = ka) {
      const o = i * 6;
      position[o] = a.x; position[o + 1] = a.y; position[o + 2] = a.z;
      position[o + 3] = b.x; position[o + 4] = b.y; position[o + 5] = b.z;
      color[o] = ca.r * ka; color[o + 1] = ca.g * ka; color[o + 2] = ca.b * ka;
      color[o + 3] = cb.r * kb; color[o + 4] = cb.g * kb; color[o + 5] = cb.b * kb;
    },

    flush(count = maxSegments) {
      geometry.setDrawRange(0, count * 2);
      geometry.attributes.position.needsUpdate = true;
      geometry.attributes.color.needsUpdate = true;
    }
  };
}

function circle(radius, color, opacity, segments = 160) {
  const points = [];
  for (let i = 0; i < segments; i++) {
    const a = i / segments * Math.PI * 2;
    points.push(new THREE.Vector3(Math.cos(a) * radius, Math.sin(a) * radius, 0));
  }
  return new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints(points),
    new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    })
  );
}

function wireframe(geometry, color, opacity) {
  return new THREE.Mesh(
    geometry,
    new THREE.MeshBasicMaterial({
      color,
      wireframe: true,
      transparent: true,
      opacity,
      depthWrite: false
    })
  );
}

/*
 * Position an HTML label over a point in a scene.
 * Returns the point's world-space depth so callers can dim
 * labels that sit on the far side of a rotating object.
 */
const projected = new THREE.Vector3();

function placeLabel(stage, element, object, local) {
  projected.copy(local);
  if (object) projected.applyMatrix4(object.matrixWorld);
  const depth = projected.z;

  projected.project(stage.camera);

  const x = (projected.x * 0.5 + 0.5) * stage.width;
  const y = (-projected.y * 0.5 + 0.5) * stage.height;

  element.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
  return depth;
}

const rand = (min = 0, max = 1) => min + Math.random() * (max - min);

const ease = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const clamp01 = t => Math.max(0, Math.min(1, t));

function bezier(out, a, control, b, t) {
  const u = 1 - t;
  return out.set(
    u * u * a.x + 2 * u * t * control.x + t * t * b.x,
    u * u * a.y + 2 * u * t * control.y + t * t * b.y,
    u * u * a.z + 2 * u * t * control.z + t * t * b.z
  );
}

function randomOnSphere(radius = 1, out = new THREE.Vector3()) {
  const theta = Math.random() * Math.PI * 2;
  const phi = Math.acos(2 * Math.random() - 1);
  return out.setFromSphericalCoords(radius, phi, theta);
}

function randomInSphere(radius = 1, out = new THREE.Vector3()) {
  return randomOnSphere(radius * Math.cbrt(Math.random()), out);
}

function fibonacciSphere(count, radius = 1) {
  const points = [];
  for (let i = 0; i < count; i++) {
    const y = 1 - 2 * (i + 0.5) / count;
    const r = Math.sqrt(1 - y * y);
    const a = i * 2.399963;
    points.push(new THREE.Vector3(Math.cos(a) * r * radius, y * radius, Math.sin(a) * r * radius));
  }
  return points;
}

export default {
  THREE,
  reducedMotion,
  palette,
  createStage,
  glowPoints,
  glowLines,
  circle,
  wireframe,
  placeLabel,
  rand,
  ease,
  clamp01,
  bezier,
  randomOnSphere,
  randomInSphere,
  fibonacciSphere
};
